import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDownIcon, Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';

import CoachLoginDrawer from './CoachLoginDrawer';
import ClientLoginDrawer from './ClientLoginDrawer';
export default function AppHeader() {
  const location = useLocation();
  const path = location.pathname;

  // Only show the header on marketing/auth pages; every other route is an app page
  const SHOW_HEADER_PATHS = ['/', '/login', '/admin/login', '/register/provider',
    '/reset-password', '/verify', '/terms', '/privacy', '/ai-usage-data', '/help'];
  const isVisibleRoute = SHOW_HEADER_PATHS.includes(path) || path.startsWith('/help/') || path.startsWith('/book');

  const isMarketing = path === '/';
  const isLoginPage = path === '/login' || path === '/admin/login';
  const showActions = !isLoginPage;

  const [signInOpen, setSignInOpen] = useState(false);
  const [coachLoginOpen, setCoachLoginOpen] = useState(false);
  const [clientLoginOpen, setClientLoginOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  if (!isVisibleRoute) return null;

  return (
    <>
      <CoachLoginDrawer isOpen={coachLoginOpen} onClose={() => setCoachLoginOpen(false)} />
      <ClientLoginDrawer isOpen={clientLoginOpen} onClose={() => setClientLoginOpen(false)} />

      <header className={`fixed top-0 left-0 right-0 z-50 shadow-lg flex items-center border-b h-[100px] ${isMarketing ? 'bg-white border-gray-200' : 'bg-dark-sidebar border-dark-border'}`}>

        {/* Logo + tagline */}
        <div className={`flex-shrink-0 flex flex-col ${!isMarketing ? 'bg-white border-r border-gray-200' : ''}`} style={{ width: 'clamp(140px, 40vw, 224px)' }}>
          <Link to="/" className="overflow-hidden block" style={{ width: '100%', height: 'clamp(44px, 12vw, 72px)' }}>
            <img
              src="/coachsight-logo.png"
              alt="CoachSight ECR"
              style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center 50%' }}
            />
          </Link>
          <span className="block w-full text-center text-purple-600 text-[11px] md:text-[13px] font-bold tracking-widest uppercase py-1 border-t border-gray-100">
            See Beyond the Session
          </span>
        </div>

        {/* Remaining header content — padded */}
        <div className="flex flex-1 items-center px-6">

        {/* Nav links — absolutely centered so logo/button widths don't affect alignment */}
        {isMarketing && (
          <nav className="hidden md:flex items-center gap-7 text-sm text-purple-600 font-medium absolute left-1/2 -translate-x-1/2">
            <a href="/" className="hover:text-purple-900 transition-colors">Home</a>
            <a href="#features" className="hover:text-purple-900 transition-colors">Features</a>
            <a href="#why" className="hover:text-purple-900 transition-colors">Why CoachSight</a>
            <a href="#ai-trust" className="hover:text-purple-900 transition-colors">Responsible AI</a>
            <a href="#about" className="hover:text-purple-900 transition-colors">About</a>
            <a href="#pricing" className="hover:text-purple-900 transition-colors">Pricing</a>
            <a href="#faq" className="hover:text-purple-900 transition-colors">FAQ</a>
          </nav>
        )}

        {/* Dashboard right: Help */}
        {/* Hamburger — mobile only, marketing page only */}
        {isMarketing && (
          <button
            className="md:hidden ml-auto p-2 text-purple-600"
            onClick={() => setMobileMenuOpen(o => !o)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <XMarkIcon className="w-6 h-6" /> : <Bars3Icon className="w-6 h-6" />}
          </button>
        )}

        {/* Action buttons — right, desktop only */}
        {showActions && (
          <div className="hidden md:flex items-center gap-3 ml-auto">
            {/* Register as Coach */}
            <Link
              to="/register/provider"
              className="text-sm font-semibold rounded-full px-4 py-1.5 transition-colors bg-purple-600 hover:bg-purple-700 text-white"
            >
              Join as Coach
            </Link>

            {/* Sign In — outlined */}
            <div className="relative">
              <button
                onClick={() => setSignInOpen(o => !o)}
                className={`flex items-center gap-1.5 text-sm font-semibold rounded-full px-4 py-1.5 transition-colors border ${isMarketing ? 'text-purple-600 border-purple-300 hover:border-purple-600 hover:text-purple-900' : 'text-gray-200 border-gray-500 hover:border-primary-400 hover:text-primary-400'}`}
              >
                Sign In
                <ChevronDownIcon className={`w-3.5 h-3.5 transition-transform ${signInOpen ? 'rotate-180' : ''}`} />
              </button>
              {signInOpen && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setSignInOpen(false)} />
                  <div className={`absolute right-0 mt-2 w-44 rounded-xl shadow-lg z-20 overflow-hidden border ${isMarketing ? 'bg-white border-gray-200' : 'bg-dark-card border-dark-border'}`}>
                    <button
                      onClick={() => { setSignInOpen(false); setCoachLoginOpen(true); }}
                      className={`w-full text-left px-4 py-3 text-sm transition-colors ${isMarketing ? 'text-purple-600 hover:bg-purple-50 hover:text-purple-900' : 'text-gray-300 hover:bg-dark-hover hover:text-primary-400'}`}
                    >
                      Coach Sign In
                    </button>
                    <div className={`border-t ${isMarketing ? 'border-gray-100' : 'border-dark-border'}`} />
                    <button
                      onClick={() => { setSignInOpen(false); setClientLoginOpen(true); }}
                      className={`w-full text-left px-4 py-3 text-sm transition-colors ${isMarketing ? 'text-purple-600 hover:bg-purple-50 hover:text-purple-900' : 'text-gray-300 hover:bg-dark-hover hover:text-primary-400'}`}
                    >
                      Client Sign In
                    </button>
                  </div>
                </>
              )}
            </div>

          </div>
        )}
        </div>
      </header>

      {/* Mobile nav menu */}
      {isMarketing && mobileMenuOpen && (
        <div className="md:hidden fixed top-[100px] left-0 right-0 bg-white border-b border-gray-200 shadow-lg z-40 py-4 px-6 flex flex-col gap-4">
          {[
            { href: '/', label: 'Home' },
            { href: '#features', label: 'Features' },
            { href: '#why', label: 'Why CoachSight' },
            { href: '#ai-trust', label: 'Responsible AI' },
            { href: '#about', label: 'About' },
            { href: '#pricing', label: 'Pricing' },
            { href: '#faq', label: 'FAQ' },
          ].map(({ href, label }) => (
            <a
              key={href}
              href={href}
              onClick={() => setMobileMenuOpen(false)}
              className="text-purple-600 font-medium text-sm hover:text-purple-900 transition-colors"
            >
              {label}
            </a>
          ))}
          <div className="border-t border-gray-100 pt-3 flex flex-col gap-3">
            <Link
              to="/register/provider"
              onClick={() => setMobileMenuOpen(false)}
              className="text-left text-sm font-semibold text-purple-600"
            >
              Join as Coach
            </Link>
            <button
              onClick={() => { setMobileMenuOpen(false); setCoachLoginOpen(true); }}
              className="text-left text-sm font-semibold text-purple-600"
            >
              Coach Sign In
            </button>
            <button
              onClick={() => { setMobileMenuOpen(false); setClientLoginOpen(true); }}
              className="text-left text-sm font-semibold text-purple-600"
            >
              Client Sign In
            </button>
          </div>
        </div>
      )}
    </>
  );
}
